import React from 'react';
import { useTranslation } from 'react-i18next';
import StatusPill from '../ui/StatusPill';
import { Hourglass } from '../ui/Icons';
import {
  getRequestIcon,
  getRequestLabel,
  formatTimeAgo,
  getAgeMinutes,
  getWaitTone
} from './serviceRequestHelpers';

/**
 * Presentational service-request card for the standalone manager page.
 * Wider than the dashboard widget's row: shows a status pill, an escalating
 * wait-time indicator (calm → warning → danger), the optional table message,
 * and the acknowledge / complete actions.
 */
const ServiceRequestCard = ({ request, onAcknowledge, onComplete }) => {
  const { t } = useTranslation();
  const minutes = getAgeMinutes(request.created_at);
  const tone = getWaitTone(minutes, request.priority);
  const isPending = request.status === 'pending';
  const isUrgent = request.priority === 'high' || request.priority === 'urgent';

  return (
    <article
      className={`sr-card sr-tone-${tone} sr-status-${request.status}${
        isPending && tone === 'danger' ? ' sr-card-pulse' : ''
      }`}
    >
      <div className="sr-card-top">
        <span className="sr-card-icon" aria-hidden="true">
          {getRequestIcon(request.request_type)}
        </span>
        <div className="sr-card-info">
          <h3 className="sr-card-title">
            {t('common.table', { defaultValue: 'Table' })} {request.table_number}
          </h3>
          <p className="sr-card-type">{getRequestLabel(t, request.request_type)}</p>
        </div>
        <StatusPill
          status={request.status}
          label={t(`serviceRequests.status.${request.status}`, {
            defaultValue: request.status,
          })}
          size="sm"
          dot
        />
      </div>

      {request.message && (
        <p className="sr-card-message">&ldquo;{request.message}&rdquo;</p>
      )}

      <div className="sr-card-foot">
        <span className={`sr-wait sr-wait-${tone}`}>
          <Hourglass size={14} />
          {formatTimeAgo(t, request.created_at)}
          {isUrgent && (
            <span className="sr-urgent-flag">
              {t('serviceRequests.urgent', { defaultValue: 'Urgent' })}
            </span>
          )}
        </span>

        <div className="sr-card-actions">
          {isPending && (
            <button
              type="button"
              className="sr-btn sr-btn-ack"
              onClick={() => onAcknowledge(request.id)}
            >
              {t('service.panel.acknowledge', { defaultValue: 'Acknowledge' })}
            </button>
          )}
          <button
            type="button"
            className="sr-btn sr-btn-done"
            onClick={() => onComplete(request.id)}
          >
            {request.status === 'acknowledged'
              ? t('service.panel.complete', { defaultValue: 'Complete' })
              : t('service.panel.done', { defaultValue: 'Done' })}
          </button>
        </div>
      </div>
    </article>
  );
};

export default ServiceRequestCard;
